import { useMemo, useState } from "react";
import { FullViewModal } from "../../components/FullViewModal.jsx";

// Two different counts:
//   header           - viewerCount: who is watching right now, the same live count as the
//                      Viewers card (useViewerCount); null until it has been read
//   Participant list - the HCP viewers on the users channel (useHcpUsers): everyone who has
//                      opened the session, each with their latest status and a Join / Remove
//                      that changes only that viewer
// hasMeetingVendor: the session has a meeting (hosting.vendor) - without one a joined viewer
// has nothing to switch to.
// The search box filters the list in the browser only, by name or clientID.
export function ViewersModal({
  users,
  viewerCount = null,
  hasMeetingVendor,
  onJoin,
  onRemove,
  onClose,
}) {
  const [query, setQuery] = useState("");
  const term = query.trim().toLowerCase();
  const visibleUsers = useMemo(
    () =>
      term
        ? users.filter(
            (user) =>
              (user.name || "").toLowerCase().includes(term) ||
              (user.clientID || "").toLowerCase().includes(term),
          )
        : users,
    [users, term],
  );

  return (
    <FullViewModal
      labelId="viewers-modal-title"
      title={<span className="text-base font-semibold text-gray-800">Viewers</span>}
      subtitle={
        <p className="flex items-center gap-1.5 text-xs text-gray-500">
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 rounded-full ${viewerCount ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`}
          />
          Watching right now:
          <span className="font-semibold text-gray-800 tabular-nums" aria-label="Live viewer count">
            {viewerCount == null ? "—" : viewerCount.toLocaleString()}
          </span>
        </p>
      }
      onClose={onClose}
      closeOnBackdrop={false}
    >
      {!hasMeetingVendor && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This session has no meeting vendor, so a joined viewer keeps watching
          the stream until one is set.
        </p>
      )}
      <section aria-labelledby="participant-list-title">
        <div className="relative mb-4">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or ID"
            aria-label="Search participants"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>
        <div className="flex items-center gap-2">
          <h4 id="participant-list-title" className="text-sm font-semibold text-gray-800">
            Participant list
          </h4>
          <span
            className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 tabular-nums"
            aria-label="Participant count"
          >
            {term
              ? `${visibleUsers.length.toLocaleString()} / ${users.length.toLocaleString()}`
              : users.length.toLocaleString()}
          </span>
        </div>
        <p className="mt-0.5 text-[11px] text-gray-500">
          Everyone who has opened this session.
        </p>
        {users.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">
            No HCP viewer has opened the session yet.
          </p>
        ) : visibleUsers.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">
            No participant matches “{query.trim()}”.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100">
            {visibleUsers.map((user) => {
              const label = user.name || user.clientID;
              return (
                <li
                  key={user.clientID}
                  aria-label={label}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3"
                >
                  <div className="min-w-0 flex-1 basis-40">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {user.name || "Unnamed viewer"}
                    </p>
                    <p className="truncate font-mono text-[11px] text-gray-500">
                      {user.clientID}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      user.joinMeeting
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-1.5 w-1.5 rounded-full ${user.joinMeeting ? "bg-emerald-500" : "bg-gray-400"}`}
                    />
                    {user.joinMeeting ? "In meeting" : "Watching stream"}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onJoin(user.clientID)}
                      disabled={user.joinMeeting}
                      aria-label={`Join ${label}`}
                      className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Join
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemove(user.clientID)}
                      disabled={!user.joinMeeting}
                      aria-label={`Remove ${label}`}
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </FullViewModal>
  );
}

export default ViewersModal;
